import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tvcabsat_chatbot_session";
// sessionStorage is preferred over localStorage so the conversation reference
// is auto-cleared when the tab closes (smaller XSS attack surface, no long-
// lived persistent ID).
const sessionStore = typeof window !== "undefined" ? window.sessionStorage : null;

const makeMessageId = () =>
  `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const STRINGS = {
  pt: {
    open_label: "Abrir a Sofia (assistente)",
    title: "Sofia · Assistente TVCabSat",
    subtitle: "Pergunte sobre serviços, marcas ou contactos",
    placeholder: "Escreva a sua pergunta…",
    welcome:
      "Olá! Sou a Sofia, a assistente da TVCabSat. Como posso ajudar?",
    quickGroups: [
      {
        label: "Comunicações",
        items: [
          "Rádios Motorola ou Hytera para a minha equipa",
          "Central telefónica (PABX) para a empresa",
        ],
      },
      {
        label: "Redes & TV",
        items: [
          "Rede e Wi-Fi Cisco para o escritório",
          "DSTV para casa ou para hotel",
        ],
      },
      {
        label: "Satélite & Segurança",
        items: [
          "Internet VSAT para um local remoto",
          "Câmaras CFTV e alarmes Intelbras",
        ],
      },
      {
        label: "Começar",
        items: ["Que marcas representam?", "Quero que me contactem"],
      },
    ],
    error: "Não consegui responder agora. Tente novamente em instantes.",
    send: "Enviar",
    typing: "A escrever…",
    powered: "Powered by Claude",
    contact_cta: "Falar com um humano",
  },
  en: {
    open_label: "Open Sofia (assistant)",
    title: "Sofia · TVCabSat Assistant",
    subtitle: "Ask about services, brands or contacts",
    placeholder: "Type your question…",
    welcome: "Hi! I'm Sofia, the TVCabSat assistant. How can I help?",
    quickGroups: [
      {
        label: "Communications",
        items: [
          "Motorola or Hytera radios for my team",
          "Phone system (PBX) for the company",
        ],
      },
      {
        label: "Networks & TV",
        items: [
          "Cisco network and Wi-Fi for the office",
          "DSTV for home or hotel",
        ],
      },
      {
        label: "Satellite & Security",
        items: [
          "VSAT internet for a remote site",
          "Intelbras CCTV cameras and alarms",
        ],
      },
      {
        label: "Get started",
        items: ["Which brands do you represent?", "I'd like to be contacted"],
      },
    ],
    error: "Couldn't get an answer. Please try again.",
    send: "Send",
    typing: "Typing…",
    powered: "Powered by Claude",
    contact_cta: "Talk to a human",
  },
  es: {
    open_label: "Abrir a Sofia (asistente)",
    title: "Sofia · Asistente TVCabSat",
    subtitle: "Pregunta sobre servicios, marcas o contactos",
    placeholder: "Escribe tu pregunta…",
    welcome: "¡Hola! Soy Sofia, la asistente de TVCabSat. ¿En qué te ayudo?",
    quickGroups: [
      {
        label: "Comunicaciones",
        items: [
          "Radios Motorola o Hytera para mi equipo",
          "Centralita telefónica (PABX) para la empresa",
        ],
      },
      {
        label: "Redes & TV",
        items: [
          "Red y Wi-Fi Cisco para la oficina",
          "DSTV para casa u hotel",
        ],
      },
      {
        label: "Satélite & Seguridad",
        items: [
          "Internet VSAT para un sitio remoto",
          "Cámaras CCTV y alarmas Intelbras",
        ],
      },
      {
        label: "Empezar",
        items: ["¿Qué marcas representan?", "Quiero que me contacten"],
      },
    ],
    error: "No pude responder ahora. Inténtalo de nuevo.",
    send: "Enviar",
    typing: "Escribiendo…",
    powered: "Powered by Claude",
    contact_cta: "Hablar con un humano",
  },
};

// Render a chat message turning markdown links + bullets into safe React nodes.
// Note: keys mix the index with a content slice. Parsed strings never re-order
// so the index alone would be safe, but the content prefix silences strict
// "no-array-index-key" lint warnings and makes the keys self-documenting.
const formatLine = (line) => {
  // Bold **text**
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const partKey = `${i}:${p.slice(0, 16)}`;
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={partKey} className="font-semibold">
          {p.slice(2, -2)}
        </strong>
      );
    }
    // Inline links: [label](href)
    const linkParts = p.split(/(\[[^\]]+\]\([^)]+\))/g);
    return linkParts.map((lp, j) => {
      const m = lp.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (m) {
        return (
          <a
            key={`${partKey}-${j}-link`}
            href={m[2]}
            className="font-semibold text-blue-700 underline-offset-2 hover:underline"
          >
            {m[1]}
          </a>
        );
      }
      // Auto-link bare /#hash and /url paths
      const auto = lp.split(/(\/#[a-z:-]+|\/[a-z]+(?:\?[^\s]+)?)/g);
      return auto.map((ap, k) => {
        const segKey = `${partKey}-${j}-${k}:${ap.slice(0, 12)}`;
        if (ap.startsWith("/#") || ap.match(/^\/[a-z]/)) {
          return (
            <a
              key={segKey}
              href={ap}
              className="font-semibold text-blue-700 underline-offset-2 hover:underline"
            >
              {ap}
            </a>
          );
        }
        return <span key={segKey}>{ap}</span>;
      });
    });
  });
};

const MessageBubble = ({ role, content }) => {
  const isUser = role === "user";
  const lines = content.split("\n");
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`chatbot-msg-${role}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "rounded-br-sm bg-blue-600 text-white"
            : "rounded-bl-sm bg-slate-100 text-slate-800"
        }`}
      >
        {lines.map((line, i) => {
          const lineKey = `${i}:${line.slice(0, 24)}`;
          const trimmed = line.trim();
          if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
            return (
              <div key={lineKey} className="flex gap-2">
                <span aria-hidden className={isUser ? "text-white/80" : "text-blue-600"}>
                  •
                </span>
                <span>{formatLine(trimmed.slice(2))}</span>
              </div>
            );
          }
          if (line === "") return <div key={lineKey} className="h-2" />;
          return <div key={lineKey}>{formatLine(line)}</div>;
        })}
      </div>
    </div>
  );
};

export const ChatBot = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const s = STRINGS[lang] || STRINGS.pt;

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return sessionStore?.getItem(STORAGE_KEY) || null;
    } catch {
      // sessionStorage unavailable (private mode / disabled) — proceed
      // without persisting; backend will issue a fresh session id.
      return null;
    }
  });
  const [messages, setMessages] = useState([
    { id: makeMessageId(), role: "assistant", content: s.welcome },
  ]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Reset welcome message when language changes (only if just the welcome line).
  useEffect(() => {
    setMessages((m) => {
      if (m.length === 1 && m[0].role === "assistant") {
        return [{ id: makeMessageId(), role: "assistant", content: s.welcome }];
      }
      return m;
    });
  }, [lang, s.welcome]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pending]);

  // Focus input when opening
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    setMessages((m) => [
      ...m,
      { id: makeMessageId(), role: "user", content: trimmed },
    ]);
    setInput("");
    setPending(true);
    try {
      const { data } = await axios.post(`${API}/chatbot/message`, {
        session_id: sessionId,
        message: trimmed,
        locale: lang,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try {
          sessionStore?.setItem(STORAGE_KEY, data.session_id);
        } catch {
          // sessionStorage unavailable — non-fatal, just won't persist
          // the session id across reloads in this tab.
        }
      }
      setMessages((m) => [
        ...m,
        {
          id: makeMessageId(),
          role: "assistant",
          content: data.reply || s.error,
        },
      ]);
    } catch {
      // Network or backend error — show the localized fallback message.
      setMessages((m) => [
        ...m,
        { id: makeMessageId(), role: "assistant", content: s.error },
      ]);
    } finally {
      setPending(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setMessages([
      { id: makeMessageId(), role: "assistant", content: s.welcome },
    ]);
    setInput("");
    setPending(false);
    setSessionId(null);
    try {
      sessionStore?.removeItem(STORAGE_KEY);
    } catch {
      // sessionStorage unavailable — nothing to clear, safe to ignore.
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={s.open_label}
          data-testid="chatbot-open-btn"
          className="group fixed bottom-28 right-6 z-40 inline-flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-white text-white shadow-xl shadow-blue-600/30 ring-2 ring-blue-600 transition-all hover:scale-110 hover:shadow-blue-600/50 active:scale-95 md:bottom-[112px] md:h-[72px] md:w-[72px]"
        >
          <img
            src="/avatar-support.png"
            alt=""
            aria-hidden
            className="h-full w-full object-cover"
          />
          {/* Small message badge bottom-right */}
          <span className="absolute -bottom-0.5 -right-0.5 z-20 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-blue-600 shadow-md md:h-7 md:w-7">
            <MessageCircle className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
          </span>
          <span
            aria-hidden
            className="absolute right-0.5 top-0.5 z-20 inline-flex h-3.5 w-3.5 animate-pulse rounded-full bg-emerald-400 ring-2 ring-white"
          />
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-28 right-6 z-50 flex h-[min(640px,calc(100vh-160px))] w-[min(400px,calc(100vw-32px))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl ring-1 ring-black/5 md:bottom-[112px]"
        >
          <div className="flex items-center gap-3 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-4 py-3.5 text-white">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-lg ring-2 ring-white/40">
              <img
                src="/avatar-support.png"
                alt=""
                aria-hidden
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading text-sm font-bold">{s.title}</div>
              <div className="truncate text-[11px] text-white/80">
                {s.subtitle}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              data-testid="chatbot-close-btn"
              aria-label="Close"
              className="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-slate-50/50 to-white px-4 py-4"
          >
            {messages.map((m) => (
              <MessageBubble key={m.id} role={m.role} content={m.content} />
            ))}
            {pending && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm bg-slate-100 px-3.5 py-2.5 text-sm text-slate-500">
                  <span className="inline-flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:-0.3s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:-0.15s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500" />
                  </span>
                </div>
              </div>
            )}
            {messages.length === 1 && !pending && (
              <div className="space-y-3 pt-2">
                {s.quickGroups.map((group) => (
                  <div key={group.label} className="space-y-1.5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      {group.label}
                    </div>
                    {group.items.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => sendMessage(q)}
                        data-testid={`chatbot-quick-${q.slice(0, 12)}`}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-sm font-bold text-slate-800 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="border-t border-slate-100 bg-white p-3"
          >
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={s.placeholder}
                disabled={pending}
                data-testid="chatbot-input"
                className="flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/15"
              />
              <button
                type="submit"
                disabled={!input.trim() || pending}
                data-testid="chatbot-send-btn"
                aria-label={s.send}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-md transition-all hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between px-1 text-[10px] text-slate-400">
              <span>{s.powered}</span>
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  navigate("/#contact");
                }}
                className="font-semibold text-blue-600 hover:text-blue-700 hover:underline"
              >
                {s.contact_cta} →
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBot;
